import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import {
  plato_api_single_attempt_fitb_grading_attrs_question_path,
  plato_api_multi_attempt_fitb_grading_attrs_question_path,
} from '@/modules/routes';
import { StudentAnswerProps } from '@/components/staffer/Grading/types';
import { noAnswerProvided, renderCorrectnessIcon } from '@/components/staffer/Grading/utils';

const FillInTheBlankQuestionStudentAnswer = ({ question, questionAnswer }: StudentAnswerProps) => {
  const singleAttemptFitbAnswerRows = () => (
    question.choices_per_blank.map((_, answerOptionsIndex) => {
      const fitbGradingAttrsQuery = useQuery(
        ['fitbGrading', question.id, answerOptionsIndex],
        () => (
          Axios.get(plato_api_single_attempt_fitb_grading_attrs_question_path(question.id, {
            answer_options_index: answerOptionsIndex,
            question_answer_id: questionAnswer.id,
          }))
            .then(res => res.data)
        ),
      );

      if (fitbGradingAttrsQuery.isLoading) {
        return (
          <tr>
            <td>Loading...</td>
            <td>Loading...</td>
          </tr>
        );
      }

      return (
        <tr>
          <td>
            {renderCorrectnessIcon(fitbGradingAttrsQuery.data.is_choice_correct)}
            {fitbGradingAttrsQuery.data.student_selected_choice_text}
          </td>
          <td>
            {fitbGradingAttrsQuery.data.correct_choice_text}
          </td>
        </tr>
      );
    })
  );

  const multiAttemptFitbAnswerRows = () => (
    question.choices_per_blank.map((_, answerOptionsIndex) => (
      <tr>
        {
          [0, 1].map((attemptNumber) => {
            const studentChoiceIndexForAttempt = questionAnswer.gather_attempts[attemptNumber][answerOptionsIndex];

            const fitbGradingAttrsQuery = useQuery(
              ['fitbGrading', question.id, answerOptionsIndex, studentChoiceIndexForAttempt],
              () => (
                Axios.get(plato_api_multi_attempt_fitb_grading_attrs_question_path(question.id, {
                  answer_options_index: answerOptionsIndex,
                  question_answer_id: questionAnswer.id,
                  student_choice_index_for_attempt: studentChoiceIndexForAttempt,
                }))
                  .then(res => res.data)
              ),
            );

            if (fitbGradingAttrsQuery.isLoading) {
              return <td>LOADING</td>;
            }

            return (
              <>
                <td>
                  {renderCorrectnessIcon(fitbGradingAttrsQuery.data.is_attempt_correct)}
                  {fitbGradingAttrsQuery.data.text_for_attempt}
                </td>
                {attemptNumber === 1 && <td>{fitbGradingAttrsQuery.data.correct_choice_text}</td>}
              </>
            );
          })
        }
      </tr>
    ))
  );

  if (!questionAnswer) return noAnswerProvided();

  if (questionAnswer.gather_attempts?.length !== 1) {
    return (
      <table className="fill-in-the-blank-answer-preview answer-preview-table tw-min-w-[475px]">
        <thead>
          <tr>
            <td>Selected answer (1st)</td>
            <td>Selected answer (2nd)</td>
            <td>Correct answer</td>
          </tr>
        </thead>
        <tbody>
          {multiAttemptFitbAnswerRows()}
        </tbody>
      </table>
    );
  }

  return (
    <table className="fill-in-the-blank-answer-preview answer-preview-table tw-min-w-[475px]">
      <thead>
        <tr>
          <td>Selected answer</td>
          <td>Correct answer</td>
        </tr>
      </thead>
      <tbody>
        {singleAttemptFitbAnswerRows()}
      </tbody>
    </table>
  );
};

export default FillInTheBlankQuestionStudentAnswer;
